import { useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";

import { ACTIVE_BOOKING_URL } from "@/config/bookingLinks.js";
const associatePackages = [
  { sessions: 4, price: 475, save: 25 },
  { sessions: 8, price: 900, save: 100 },
  { sessions: 12, price: 1260, save: 240 },
];

const seniorPackages = [
  { sessions: 4, price: 760, save: 40 },
  { sessions: 8, price: 1260, save: 200 },
  { sessions: 12, price: 2040, save: 360 },
];

const packageNotes = [
  "Available to new clients only and limited to one package per client.",
  "Packages must be paid in full at the time of purchase and are non-refundable.",
  "Packages are self-pay only and are not eligible for insurance billing or courtesy billing.",
  "Sessions may be applied toward late cancellations and no-shows.",
  "Packages may be used with any therapist at the same fee level.",
  "Full package terms and expiration timelines are reviewed in the client portal before sessions begin.",
];

export default function FullPricing() {
  const [noteOpen, setNoteOpen] = useState(false);

  return (
    <>
      <title>Rates & Insurance | Perspective Therapy Larchmont NY Pricing</title>
      <meta
        name="description"
        content="Transparent therapy pricing at Perspective Therapy. Individual therapy $65-$200, couples $200 per session. Self-pay with courtesy billing. Packages available."
      />
      <PageLayout>
        {/* ═════ HERO ═════ */}
        <section className="relative w-full min-h-[480px] md:min-h-[560px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/rates and insurance/rates and insurance-hero.png"
              alt="Transparent pricing at Perspective Therapy"
              className="w-full h-full object-cover object-top"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 55%)",
              }}
            />
          </div>

          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-20">
            <div className="max-w-2xl">
              <p className="font-manrope text-white/90 text-[13px] font-semibold tracking-widest uppercase mb-4">
                Rates &amp; Payment Options
              </p>
              <h1 className="font-fraunces text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.15] mb-6">
                Know what your care costs,{" "}
                <em className="not-italic font-normal" style={{ color: "#C9BCEE" }}>
                  with no surprises
                </em>
              </h1>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 md:p-4 max-w-lg">
                <p className="font-manrope text-[#2B2B2B]/90 text-lg leading-relaxed">
                  Session rates, therapy packages, payment methods, and courtesy
                  billing. Everything you need to plan your care.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ═════ STANDARD SESSION RATES ═════ */}
        <section
          className="w-full py-16 md:py-20"
          style={{
            background: "linear-gradient(180deg, #F2EDE4 0%, #EEE8DA 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-12 md:mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-5 h-[2px] rounded-full"
                    style={{
                      background: "linear-gradient(90deg, #7D54FC, #B4A6E3)",
                    }}
                  />
                  <span
                    className="font-manrope text-[13px] font-semibold tracking-widest uppercase"
                    style={{ color: "#7D54FC" }}
                  >
                    Standard Session Rates
                  </span>
                </div>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Rates by therapy type{" "}
                  <em className="not-italic" style={{ color: "#7D54FC" }}>
                    and therapist level
                  </em>
                </h2>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed max-w-lg">
                  Rates vary by therapist experience level. Each therapist&apos;s
                  individual rate is listed on their profile page.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {/* Individual */}
              <ScrollReveal variant="fadeUp" delay={0} threshold={0.08} className="h-full">
                <div
                  className="h-full rounded-2xl border bg-white/80 card-hover overflow-hidden cursor-default"
                  style={{ borderColor: "rgba(125,84,252,0.20)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(125,84,252,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(125,84,252,0.20)";
                  }}
                >
                  <div
                    className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg, #7D54FC, #B4A6E3)" }}
                  />
                  <div className="p-7 md:p-8">
                    <div className="flex items-center gap-4 mb-5">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-full border shrink-0"
                        style={{
                          background: "rgba(125,84,252,0.08)",
                          borderColor: "rgba(125,84,252,0.18)",
                        }}
                      >
                        <i
                          className="ri-user-heart-line text-lg"
                          style={{ color: "#7D54FC" }}
                        ></i>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium">
                        Individual Therapy
                      </h3>
                    </div>

                    <div className="mb-3">
                      <span
                        className="font-fraunces text-3xl font-light"
                        style={{ color: "#7D54FC" }}
                      >
                        $65-$200
                      </span>
                      <span className="font-manrope text-xs text-[#2B2B2B]/40 ml-2">
                        per session
                      </span>
                    </div>

                    <p className="font-manrope text-[14px] text-[#2B2B2B]/75 leading-relaxed mb-5">
                      One-on-one therapy tailored to your goals, history, and pace.
                      Telehealth and in-person appointments available.
                    </p>

                    <ul className="flex flex-col gap-2">
                      {[
                        "Telehealth or in-person",
                        "Flexible scheduling",
                        "Ongoing progress-focused care",
                      ].map((f) => (
                        <li key={f} className="flex items-center gap-2.5">
                          <span className="w-4 h-4 flex items-center justify-center shrink-0">
                            <i
                              className="ri-check-line text-sm"
                              style={{ color: "#7D54FC" }}
                            ></i>
                          </span>
                          <span className="font-manrope text-[13px] text-[#2B2B2B]/80">
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </ScrollReveal>

              {/* Couples */}
              <ScrollReveal variant="fadeUp" delay={100} threshold={0.08} className="h-full">
                <div
                  className="h-full rounded-2xl border bg-white/80 card-hover overflow-hidden cursor-default"
                  style={{ borderColor: "rgba(60,111,248,0.20)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(60,111,248,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(60,111,248,0.20)";
                  }}
                >
                  <div
                    className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg, #3C6FF8, #7D54FC)" }}
                  />
                  <div className="p-7 md:p-8">
                    <div className="flex items-center gap-4 mb-5">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-full border shrink-0"
                        style={{
                          background: "rgba(60,111,248,0.08)",
                          borderColor: "rgba(60,111,248,0.18)",
                        }}
                      >
                        <i
                          className="ri-hearts-line text-lg"
                          style={{ color: "#3C6FF8" }}
                        ></i>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium">
                        Couples Therapy
                      </h3>
                    </div>

                    <div className="mb-3">
                      <span
                        className="font-fraunces text-3xl font-light"
                        style={{ color: "#3C6FF8" }}
                      >
                        $200
                      </span>
                      <span className="font-manrope text-xs text-[#2B2B2B]/40 ml-2">
                        per session
                      </span>
                    </div>

                    <p className="font-manrope text-[14px] text-[#2B2B2B]/75 leading-relaxed mb-5">
                      Joint sessions for partners working through conflict,
                      communication, and connection. Structured and evidence-based.
                    </p>

                    <ul className="flex flex-col gap-2">
                      {[
                        "Telehealth or in-person",
                        "Gottman-informed approach",
                        "Flexible scheduling",
                      ].map((f) => (
                        <li key={f} className="flex items-center gap-2.5">
                          <span className="w-4 h-4 flex items-center justify-center shrink-0">
                            <i
                              className="ri-check-line text-sm"
                              style={{ color: "#3C6FF8" }}
                            ></i>
                          </span>
                          <span className="font-manrope text-[13px] text-[#2B2B2B]/80">
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* ═════ SESSION LENGTH ═════ */}
        <section
          className="w-full py-16 md:py-20"
          style={{
            background: "linear-gradient(180deg, #EEE8DA 0%, #F2EDE4 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-12 md:mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-5 h-[2px] rounded-full"
                    style={{
                      background: "linear-gradient(90deg, #6B742F, #B4A6E3)",
                    }}
                  />
                  <span
                    className="font-manrope text-[13px] font-semibold tracking-widest uppercase"
                    style={{ color: "#6B742F" }}
                  >
                    Session Length
                  </span>
                </div>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Standard and extended{" "}
                  <em className="not-italic" style={{ color: "#7D54FC" }}>
                    session options
                  </em>
                </h2>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed max-w-lg">
                  Standard sessions are 45-50 minutes. Clients may request a double
                  session when clinically appropriate.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {/* Standard */}
              <ScrollReveal variant="fadeUp" delay={0} threshold={0.08}>
                <div
                  className="rounded-2xl border bg-white/80 card-hover overflow-hidden cursor-default"
                  style={{ borderColor: "rgba(107,116,47,0.20)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(107,116,47,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(107,116,47,0.20)";
                  }}
                >
                  <div
                    className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg, #6B742F, #B4A6E3)" }}
                  />
                  <div className="p-7 md:p-8">
                    <div className="flex items-center gap-4 mb-5">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-full border shrink-0"
                        style={{
                          background: "rgba(107,116,47,0.08)",
                          borderColor: "rgba(107,116,47,0.18)",
                        }}
                      >
                        <i
                          className="ri-time-line text-lg"
                          style={{ color: "#6B742F" }}
                        ></i>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium">
                        Standard Sessions
                      </h3>
                    </div>
                    <p className="font-manrope text-[14px] text-[#2B2B2B]/75 leading-relaxed">
                      Standard sessions are 45-50 minutes. This is the typical
                      length for individual and couples therapy appointments. Most
                      clients find this timeframe sufficient to cover meaningful work
                      while maintaining focus and energy.
                    </p>
                  </div>
                </div>
              </ScrollReveal>

              {/* Double */}
              <ScrollReveal variant="fadeUp" delay={100} threshold={0.08}>
                <div
                  className="rounded-2xl border bg-white/80 card-hover overflow-hidden cursor-default"
                  style={{ borderColor: "rgba(60,111,248,0.20)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(60,111,248,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(60,111,248,0.20)";
                  }}
                >
                  <div
                    className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg, #3C6FF8, #7D54FC)" }}
                  />
                  <div className="p-7 md:p-8">
                    <div className="flex items-center gap-4 mb-5">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-full border shrink-0"
                        style={{
                          background: "rgba(60,111,248,0.08)",
                          borderColor: "rgba(60,111,248,0.18)",
                        }}
                      >
                        <i
                          className="ri-expand-left-right-line text-lg"
                          style={{ color: "#3C6FF8" }}
                        ></i>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium">
                        Double Sessions
                      </h3>
                    </div>
                    <p className="font-manrope text-[14px] text-[#2B2B2B]/75 leading-relaxed mb-4">
                      Clients may request a double session when clinically
                      appropriate. A double session is billed at twice the standard
                      session rate.
                    </p>
                    <div
                      className="rounded-lg px-4 py-3"
                      style={{
                        background: "rgba(125,84,252,0.05)",
                        border: "1px solid rgba(125,84,252,0.12)",
                      }}
                    >
                      <p className="font-manrope text-[13px] text-[#2B2B2B]/70 leading-relaxed">
                        <i
                          className="ri-information-line mr-1.5"
                          style={{ color: "#7D54FC" }}
                        ></i>
                        If using out-of-network benefits, insurance typically does
                        not reimburse for sessions longer than 60 minutes on the same
                        day.
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* ═════ THERAPY PACKAGES ═════ */}
        <section
          className="w-full py-16 md:py-20"
          style={{
            background: "linear-gradient(180deg, #F2EDE4 0%, #EEE8DA 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-12 md:mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-5 h-[2px] rounded-full"
                    style={{
                      background: "linear-gradient(90deg, #6B742F, #B4A6E3)",
                    }}
                  />
                  <span
                    className="font-manrope text-[13px] font-semibold tracking-widest uppercase"
                    style={{ color: "#6B742F" }}
                  >
                    Therapy Packages
                  </span>
                </div>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Save with bundled{" "}
                  <em className="not-italic" style={{ color: "#7D54FC" }}>
                    session packages
                  </em>
                </h2>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed max-w-lg">
                  Available for new clients who want consistency in weekly therapy
                  and prefer to pay upfront. Packages are self-pay only and are not
                  eligible for insurance billing.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-10">
              {/* Associate - Blue */}
              <ScrollReveal variant="fadeUp" delay={0} threshold={0.08}>
                <div
                  className="rounded-2xl border bg-white/80 card-hover overflow-hidden cursor-default"
                  style={{ borderColor: "rgba(60,111,248,0.20)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(60,111,248,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(60,111,248,0.20)";
                  }}
                >
                  <div
                    className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg, #3C6FF8, #7D54FC)" }}
                  />
                  <div className="p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4 mb-5 md:mb-6">
                      <div
                        className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full border shrink-0"
                        style={{
                          background: "rgba(60,111,248,0.08)",
                          borderColor: "rgba(60,111,248,0.18)",
                        }}
                      >
                        <i
                          className="ri-graduation-cap-line text-base md:text-lg"
                          style={{ color: "#3C6FF8" }}
                        ></i>
                      </div>
                      <div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-lg md:text-xl font-medium">
                          Associate Psychotherapist
                        </h3>
                        <p className="font-manrope text-[12px] md:text-[13px] text-[#2B2B2B]/55">
                          Supervised clinicians
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 md:gap-3">
                      {associatePackages.map((pkg) => (
                        <div
                          key={pkg.sessions}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 rounded-xl px-4 md:px-5 py-3 md:py-4"
                          style={{ background: "rgba(60,111,248,0.04)" }}
                        >
                          <span className="font-manrope text-[13px] font-semibold text-[#2B2B2B]/50">
                            {pkg.sessions} sessions
                          </span>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="font-manrope text-[12px] md:text-[13px] text-[#2B2B2B]/40 line-through">
                              ${(pkg.price + pkg.save).toLocaleString()}
                            </span>
                            <span
                              className="font-manrope text-[12px] md:text-[13px] font-semibold"
                              style={{ color: "#3C6FF8" }}
                            >
                              save ${pkg.save}
                            </span>
                            <span
                              className="font-fraunces text-base md:text-lg font-light"
                              style={{ color: "#3C6FF8" }}
                            >
                              ${pkg.price.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollReveal>

              {/* Senior - Purple */}
              <ScrollReveal variant="fadeUp" delay={100} threshold={0.08}>
                <div
                  className="rounded-2xl border bg-white/80 card-hover overflow-hidden cursor-default"
                  style={{ borderColor: "rgba(125,84,252,0.20)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(125,84,252,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(125,84,252,0.20)";
                  }}
                >
                  <div
                    className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg, #7D54FC, #B4A6E3)" }}
                  />
                  <div className="p-5 md:p-8">
                    <div className="flex items-center gap-3 md:gap-4 mb-5 md:mb-6">
                      <div
                        className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full border shrink-0"
                        style={{
                          background: "rgba(125,84,252,0.08)",
                          borderColor: "rgba(125,84,252,0.18)",
                        }}
                      >
                        <i
                          className="ri-award-line text-base md:text-lg"
                          style={{ color: "#7D54FC" }}
                        ></i>
                      </div>
                      <div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-lg md:text-xl font-medium">
                          Senior Psychotherapist
                        </h3>
                        <p className="font-manrope text-[12px] md:text-[13px] text-[#2B2B2B]/55">
                          More experienced therapists
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 md:gap-3">
                      {seniorPackages.map((pkg) => (
                        <div
                          key={pkg.sessions}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 rounded-xl px-4 md:px-5 py-3 md:py-4"
                          style={{ background: "rgba(125,84,252,0.04)" }}
                        >
                          <span className="font-manrope text-[13px] font-semibold text-[#2B2B2B]/50">
                            {pkg.sessions} sessions
                          </span>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="font-manrope text-[12px] md:text-[13px] text-[#2B2B2B]/40 line-through">
                              ${(pkg.price + pkg.save).toLocaleString()}
                            </span>
                            <span
                              className="font-manrope text-[12px] md:text-[13px] font-semibold"
                              style={{ color: "#7D54FC" }}
                            >
                              save ${pkg.save}
                            </span>
                            <span
                              className="font-fraunces text-base md:text-lg font-light"
                              style={{ color: "#7D54FC" }}
                            >
                              ${pkg.price.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Package Terms Accordion */}
            <ScrollReveal variant="fadeUp" delay={200} threshold={0.08}>
              <div
                className="rounded-2xl border bg-white/80 overflow-hidden"
                style={{ borderColor: "rgba(203,191,175,0.30)" }}
              >
                <button
                  onClick={() => setNoteOpen(!noteOpen)}
                  className="w-full flex items-center justify-between p-5 md:p-7 cursor-pointer gap-3"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-full border shrink-0"
                      style={{
                        background: "rgba(107,116,47,0.08)",
                        borderColor: "rgba(107,116,47,0.18)",
                      }}
                    >
                      <i
                        className="ri-file-list-3-line text-base"
                        style={{ color: "#6B742F" }}
                      ></i>
                    </div>
                    <span className="font-fraunces text-[#2B2B2B] text-base md:text-lg font-medium text-left">
                      View all package terms
                    </span>
                  </div>
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-full shrink-0"
                    style={{ background: "rgba(43,43,43,0.06)" }}
                  >
                    <i
                      className={`ri-arrow-down-s-line text-lg text-[#2B2B2B]/50 transition-transform duration-200 ${
                        noteOpen ? "rotate-180" : ""
                      }`}
                    ></i>
                  </div>
                </button>

                {noteOpen && (
                  <div className="px-5 md:px-7 pb-5 md:pb-7">
                    <div
                      className="border-t pt-5"
                      style={{ borderColor: "rgba(203,191,175,0.25)" }}
                    >
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {packageNotes.map((item, i) => {
                          const colors = [
                            "#B4A6E3",
                            "#7D54FC",
                            "#3C6FF8",
                            "#6B742F",
                            "#B4A6E3",
                            "#7D54FC",
                          ];
                          return (
                            <li key={i} className="flex items-start gap-3">
                              <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                                <i
                                  className="ri-check-line text-xs"
                                  style={{ color: colors[i] }}
                                ></i>
                              </span>
                              <span className="font-manrope text-[12px] md:text-[13px] text-[#2B2B2B]/78 leading-relaxed">
                                {item}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ═════ INSURANCE & COURTESY BILLING ═════ */}
        <section
          className="w-full py-16 md:py-20"
          style={{
            background: "linear-gradient(180deg, #EEE8DA 0%, #F2EDE4 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-12 md:mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-5 h-[2px] rounded-full"
                    style={{
                      background: "linear-gradient(90deg, #3C6FF8, #7D54FC)",
                    }}
                  />
                  <span
                    className="font-manrope text-[13px] font-semibold tracking-widest uppercase"
                    style={{ color: "#3C6FF8" }}
                  >
                    Insurance &amp; Courtesy Billing
                  </span>
                </div>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Self-pay with{" "}
                  <em className="not-italic" style={{ color: "#7D54FC" }}>
                    courtesy billing support
                  </em>
                </h2>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl">
              {/* Why self-pay */}
              <ScrollReveal variant="fadeUp" delay={0} threshold={0.08}>
                <div
                  className="rounded-2xl border bg-white/80 card-hover overflow-hidden cursor-default h-full"
                  style={{ borderColor: "rgba(107,116,47,0.20)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(107,116,47,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(107,116,47,0.20)";
                  }}
                >
                  <div
                    className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg, #6B742F, #B4A6E3)" }}
                  />
                  <div className="p-7 md:p-8">
                    <div className="flex items-start gap-4 mb-5">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-full border shrink-0"
                        style={{
                          background: "rgba(107,116,47,0.08)",
                          borderColor: "rgba(107,116,47,0.18)",
                        }}
                      >
                        <i
                          className="ri-lightbulb-line text-lg"
                          style={{ color: "#6B742F" }}
                        ></i>
                      </div>
                      <div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium mb-2">
                          Why self-pay?
                        </h3>
                        <p className="font-manrope text-[14px] text-[#2B2B2B]/75 leading-relaxed">
                          Self-pay puts you in control of your care. No
                          insurance-driven session limits, no required diagnosis on
                          your medical records, and no restrictions on which therapist
                          you can see. You choose your therapist based on fit, not
                          network availability.
                        </p>
                      </div>
                    </div>

                    <ul className="flex flex-col gap-2">
                      {[
                        "No session caps or pre-authorization delays",
                        "Your records stay private - no diagnosis required",
                        "Choose any therapist based on fit, not network",
                        "Transparent pricing with no surprise bills",
                      ].map((f) => (
                        <li key={f} className="flex items-center gap-2.5">
                          <span className="w-4 h-4 flex items-center justify-center shrink-0">
                            <i
                              className="ri-check-line text-sm"
                              style={{ color: "#6B742F" }}
                            ></i>
                          </span>
                          <span className="font-manrope text-[13px] text-[#2B2B2B]/80">
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </ScrollReveal>

              {/* Courtesy billing */}
              <ScrollReveal variant="fadeUp" delay={100} threshold={0.08}>
                <div
                  className="rounded-2xl border bg-white/80 card-hover overflow-hidden cursor-default h-full"
                  style={{ borderColor: "rgba(60,111,248,0.20)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(60,111,248,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(60,111,248,0.20)";
                  }}
                >
                  <div
                    className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg, #3C6FF8, #7D54FC)" }}
                  />
                  <div className="p-7 md:p-8">
                    <div className="flex items-start gap-4 mb-5">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-full border shrink-0"
                        style={{
                          background: "rgba(60,111,248,0.08)",
                          borderColor: "rgba(60,111,248,0.18)",
                        }}
                      >
                        <i
                          className="ri-shield-check-line text-lg"
                          style={{ color: "#3C6FF8" }}
                        ></i>
                      </div>
                      <div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium mb-2">
                          Self-pay with courtesy billing
                        </h3>
                        <p className="font-manrope text-[14px] text-[#2B2B2B]/75 leading-relaxed">
                          Perspective Therapy is a self-pay and out-of-network
                          practice. We offer courtesy billing for standard sessions.
                          We bill you directly and submit claims to your insurance for
                          potential reimbursement, so you do not have to manage the
                          process yourself.
                        </p>
                      </div>
                    </div>
                    <div
                      className="rounded-xl px-5 py-4"
                      style={{
                        background: "rgba(125,84,252,0.05)",
                        border: "1px solid rgba(125,84,252,0.12)",
                      }}
                    >
                      <p className="font-manrope text-[13px] text-[#2B2B2B]/70 leading-relaxed">
                        <i
                          className="ri-information-line mr-1.5"
                          style={{ color: "#7D54FC" }}
                        ></i>
                        Therapy packages are self-pay only and are not eligible for
                        insurance billing or courtesy billing.
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Good Faith Estimate reference */}
            <ScrollReveal variant="fadeUp" delay={200} threshold={0.08}>
              <div
                className="mt-8 rounded-xl border bg-white/60 card-hover cursor-default"
                style={{ borderColor: "rgba(203,191,175,0.35)" }}
              >
                <div className="p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-full border shrink-0"
                    style={{
                      background: "rgba(107,116,47,0.08)",
                      borderColor: "rgba(107,116,47,0.18)",
                    }}
                  >
                    <i
                      className="ri-file-paper-2-line text-base"
                      style={{ color: "#6B742F" }}
                    ></i>
                  </div>
                  <div className="flex-1">
                    <p className="font-manrope text-[14px] text-[#2B2B2B]/80 leading-relaxed">
                      Under the federal No Surprises Act, you have the right to
                      receive a{" "}
                      <strong className="text-[#2B2B2B]/90 font-semibold">
                        Good Faith Estimate
                      </strong>{" "}
                      for the expected cost of any non-emergency health care services.
                    </p>
                  </div>
                  <a
                    href="/good-faith-estimate"
                    className="inline-flex items-center gap-1.5 font-manrope font-semibold text-[13px] px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap border shrink-0"
                    style={{ color: "#2B2B2B", borderColor: "rgba(43,43,43,0.18)" }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background =
                        "#2B2B2B";
                      (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background =
                        "transparent";
                      (e.currentTarget as HTMLAnchorElement).style.color = "#2B2B2B";
                    }}
                  >
                    Learn more <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ═════ FINAL CTA ═════ */}
        <section
          className="w-full py-16 md:py-20"
          style={{
            background:
              "linear-gradient(180deg, #EEE8DA 0%, #F5F1E8 45%, #F8F5EE 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-2xl mx-auto text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-4">
                  Ready to choose your{" "}
                  <em className="not-italic" style={{ color: "#7D54FC" }}>
                    therapist?
                  </em>
                </h2>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-10 max-w-md mx-auto">
                  Browse our team to find the right fit, or reach out and we will help
                  match you.
                </p>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={160}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/therapists"
                    className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap btn-primary"
                    style={{ background: "#3C6FF8" }}
                  >
                    Find Your Therapist <i className="ri-arrow-right-line"></i>
                  </a>
                  <a
                    href={ACTIVE_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap btn-outline border"
                    style={{
                      color: "#2B2B2B",
                      borderColor: "rgba(43,43,43,0.18)",
                    }}
                  >
                    Book Now
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
